import * as cityService from '@features/settings/services/cityService';

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useCitiesQuery = (search = "", limit = 20, page = 1) => {
  return useQuery({
    queryKey: ["cities", search, limit, page],
    queryFn: () => cityService.fetchAllCities(search, limit, page),
  });
};

// New hook specifically for search functionality with debouncing
export const useCitiesSearchQuery = (
  search = "",
  limit = 20,
  page = 1,
  enabled = true
) => {
  return useQuery({
    queryKey: ["cities-search", search, limit, page],
    queryFn: () => cityService.fetchAllCities(search, limit, page),
    enabled: enabled && search.length >= 0, // Enable search even for empty strings to show all cities
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useCityByNameQuery = (cityName, enabled = true) => {
  return useQuery({
    queryKey: ["city-by-name", cityName],
    queryFn: () => cityService.fetchCityByName(cityName),
    enabled: enabled && !!cityName,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useUpdateCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cityService.updateCity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
    },
    onError: (error) => {
      console.error("Error updating city:", error);
    },
  });
};

export const useDeleteCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cityService.deleteCity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
    },
    onError: (error) => {
      console.error("Error deleting city:", error);
    },
  });
};

export const useBulkDeleteCities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cityService.bulkDeleteCities,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
    },
    onError: (error) => {
      console.error("Error bulk deleting cities:", error);
    },
  });
};

export const useCreateCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cityService.createCity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
    },
    onError: (error) => {
      console.error("Error creating city:", error);
    },
  });
};
